import React, { useEffect, useMemo, useState } from 'react';
import { HomeOutlined } from '@ant-design/icons';

import { FormGroupItem } from 'components/FormGroupItem';
import { FormGroup } from 'components';

import type { Firebase } from 'types/firebase';
import { styled } from 'theme';
import { CAMPAIGN_OPTIONS } from '../../../../constants';
import type { SelectOption } from 'utils/OptionUtils';

/**
 * Styling
 */
const Container = styled.div``;

/**
 * Helpers
 */
const BUSINESS_ACTIVITY_SLOTS = ['1', '2', '3'] as const;
type BusinessActivitySlot = typeof BUSINESS_ACTIVITY_SLOTS[number];

const getBusinessActivityCountry = (
  merchant: Firebase.Merchant,
  slot: BusinessActivitySlot,
  // @ts-ignore
) => merchant?.businessActivityCountries?.[slot] ?? '';

function countryCodeToFlagEmoji(code: string): string {
  if (!code || code.length !== 2) {
    return '';
  }
  const A = 0x1f1e6;
  const upper = code.toUpperCase();
  return String.fromCodePoint(
    A + upper.charCodeAt(0) - 65,
    A + upper.charCodeAt(1) - 65,
  );
}

async function loadAllCountryOptions(locale?: string): Promise<SelectOption[]> {
  const mod: any = await import('world-countries');
  const countries = (mod?.default ?? mod) as Array<{ cca2: string }>;

  const displayNames =
    typeof Intl !== 'undefined' && (Intl as any).DisplayNames
      ? new (Intl as any).DisplayNames([locale || navigator.language], {
          type: 'region',
        })
      : null;

  return countries
    .map((c) => c.cca2)
    .filter(Boolean)
    .map((code) => code.toUpperCase())
    .map((code) => {
      const name = displayNames?.of?.(code) ?? code;
      const flag = countryCodeToFlagEmoji(code);
      return { value: code, label: `${flag ? `${flag} ` : ''}${name}` };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

function useCountryOptions() {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    loadAllCountryOptions()
      .then((list) => {
        if (!mounted) {
          return;
        }
        setOptions(list);
      })
      .finally(() => {
        if (!mounted) {
          return;
        }
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return { options, loading };
}

/**
 * Types
 */
export type MerchantNoteFormProps = {
  merchant: Firebase.Merchant;
  onSave: (name: string, value: any) => void;
};

type FormFieldType = {
  title: string;
  icon: React.ReactNode;
  inputs: Array<{
    type?: string;
    label: string;
    alternativeLabel: string;
    name: string;
    value: string | number | null;
    hasCopyIcon?: boolean;
    dropdown?: {
      placeholder: string;
      values: SelectOption[];
      options: SelectOption[];
      defaultValue?: string;
      allowClear?: boolean;
    };
  }>;
};

/**
 * MerchantNoteForm component
 */
const MerchantNoteForm: React.FC<MerchantNoteFormProps> = (props) => {
  const { merchant } = props;
  const {
    options: countryOptions,
    loading: countryLoading,
  } = useCountryOptions();

  const formFields = useMemo<FormFieldType[]>(() => {
    // IMPORTANT: For Select, the "value" must match option.value (NOT label text)
    const campaignCode =
      // @ts-ignore
      merchant?.campaign?.qpCampaign || merchant?.campaign?.noCampaign || '';

    const businessActivityCountryInputs = BUSINESS_ACTIVITY_SLOTS.map(
      (slot) => ({
        type: 'country',
        label: `Business Activity Country ${slot}`,
        alternativeLabel: `Change business activity country ${slot}`,
        name: `businessActivityCountries.${slot}`, // dot-path for Firestore
        value: getBusinessActivityCountry(merchant, slot),
        hasCopyIcon: true,
        dropdown: {
          placeholder: countryLoading
            ? 'Loading countries…'
            : 'Select a country',
          values: countryOptions,
          options: countryOptions,
          defaultValue: '',
          allowClear: slot !== '1',
        },
      }),
    );

    return [
      {
        title: 'Notes',
        icon: <HomeOutlined />,
        inputs: [
          {
            type: 'dropdown',
            label: 'Campaign',
            alternativeLabel: 'Campaign',
            name: 'campaign',
            value: campaignCode,
            dropdown: {
              placeholder: 'Select a campaign',
              values: CAMPAIGN_OPTIONS,
              options: CAMPAIGN_OPTIONS,
              defaultValue: '',
              allowClear: false,
            },
          },
          {
            type: 'textarea',
            label: 'Notes',
            alternativeLabel: 'Leave a note',
            name: 'noteAboutMerchant',
            value: merchant.noteAboutMerchant,
          },
          {
            label: 'Business Activity Description',
            alternativeLabel: 'Change the business description',
            name: 'businessActivityDescription',
            value: merchant.businessActivityDescription?.length
              ? merchant.businessActivityDescription
              : '',
            hasCopyIcon: true,
          },
          ...businessActivityCountryInputs,
        ],
      },
    ];
  }, [merchant, countryOptions, countryLoading]);

  return (
    <div>
      {formFields.map((form) => (
        <Container key={form.title}>
          <FormGroup icon={form.icon} title={form.title}>
            {form.inputs.map((input) => (
              <FormGroupItem
                {...input}
                key={input.name}
                onSave={(newValue) => props.onSave(input.name, newValue)}
              />
            ))}
          </FormGroup>
        </Container>
      ))}
    </div>
  );
};

export { MerchantNoteForm };
